import { ImageLightbox } from "@crm/ui/components/ui/image-lightbox";
import { Input } from "@crm/ui/components/ui/input";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpenCheck, CheckCircle2, Search, ZoomIn } from "lucide-react";
import { useState } from "react";
import { GUIDE_FAQS, GUIDE_SECTIONS, type GuideStep } from "./-content/guide-content";

export const Route = createFileRoute("/_protected/operation-guide/")({
  head: () => ({ meta: [{ title: "操作说明 · imiker 学员工作台" }] }),
  // 登录校验由父路由统一处理；实际工具继续使用各自的课程能力门禁。
  component: OperationGuidePage,
});

const IMAGE_ROOT = "/images/operation-guide/";

function GuideStepCard({ step, index }: { step: GuideStep; index: number }) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  return (
    <article
      className="scroll-mt-6 overflow-hidden rounded-2xl border border-border bg-card"
      id={step.id}
    >
      <div className="p-5 sm:p-7">
        <div className="mb-5 flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-soft font-semibold text-brand-text">
            {String(index + 1).padStart(2, "0")}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-lg text-ink">{step.title}</h3>
            <p className="mt-1 text-sm text-ink-2">{step.summary}</p>
          </div>
        </div>
        <ol className="space-y-3">
          {step.instructions.map((instruction, instructionIndex) => (
            <li className="flex gap-3 text-sm leading-7 text-ink-2" key={instruction}>
              <span className="shrink-0 font-semibold text-brand-text">
                {instructionIndex + 1}.
              </span>
              <span>{instruction}</span>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex items-start gap-2 rounded-xl bg-success-soft p-3 text-sm leading-6 text-ink-2">
          <CheckCircle2 aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-success" />
          <p>
            <span className="font-medium text-ink">完成后：</span>
            {step.result}
          </p>
        </div>
        {step.tip && <p className="mt-3 text-xs leading-6 text-ink-2">小提示：{step.tip}</p>}
        <Link
          className="mt-4 inline-flex items-center gap-1.5 rounded-md py-1 text-sm font-semibold text-brand-text hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          to={step.to}
        >
          {step.action}
          <ArrowRight aria-hidden="true" size={15} />
        </Link>
      </div>
      <div className="space-y-5 border-t border-border bg-muted/40 p-3 sm:p-5">
        {step.screenshots.map((screenshot, screenshotIndex) => (
          <figure key={screenshot.file}>
            <button
              aria-label={`放大截图：${screenshot.caption}`}
              className="group relative mx-auto block w-full cursor-zoom-in overflow-hidden rounded-xl border border-border bg-card focus-visible:outline-2 focus-visible:outline-ring"
              onClick={() => setPreviewIndex(screenshotIndex)}
              style={{ maxWidth: screenshot.width }}
              type="button"
            >
              <img
                alt={screenshot.caption}
                className="h-auto w-full"
                decoding="async"
                height={screenshot.height}
                loading="lazy"
                src={`${IMAGE_ROOT}${screenshot.file}`}
                width={screenshot.width}
              />
              <span className="absolute right-2 bottom-2 flex items-center gap-1 rounded-lg border border-border bg-card/95 px-2 py-1 text-xs text-ink-2 shadow-sm">
                <ZoomIn aria-hidden="true" size={14} />
                点击放大
              </span>
            </button>
            <figcaption className="mt-2 px-1 text-xs leading-6 text-ink-2">
              {screenshot.caption}
            </figcaption>
          </figure>
        ))}
      </div>
      {previewIndex !== null && (
        <ImageLightbox
          images={step.screenshots.map((item) => `${IMAGE_ROOT}${item.file}`)}
          onClose={() => setPreviewIndex(null)}
          open
          startIndex={previewIndex}
        />
      )}
    </article>
  );
}

function OperationGuidePage() {
  const [query, setQuery] = useState("");
  const search = query.trim().toLocaleLowerCase();
  const sections = GUIDE_SECTIONS.map((section) => ({
    ...section,
    steps: section.steps.filter((step) =>
      [section.title, step.title, step.summary, ...step.instructions, step.result, step.tip ?? ""]
        .join(" ")
        .toLocaleLowerCase()
        .includes(search),
    ),
  })).filter((section) => section.steps.length > 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
      <header className="mb-8">
        <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-brand-soft px-3 py-1 text-xs font-semibold text-brand-text">
          <BookOpenCheck aria-hidden="true" size={14} />
          学员使用指南
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">操作说明</h1>
        <p className="mt-3 text-sm leading-7 text-ink-2">
          从创建站点到生成内容，跟着截图一步步操作。第一次使用，建议从「开始前准备」看起。
        </p>
        <p className="mt-1 text-xs leading-6 text-ink-2">
          截图来自工具实际页面，使用示例资料；你的站点名称、模型和生成结果会有所不同。
        </p>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-brand-text">
          <Link className="hover:underline" to="/site-tools/settings">
            建站 AI 设置 →
          </Link>
          <Link className="hover:underline" to="/sio-tools/settings">
            推广 AI 设置 →
          </Link>
        </div>
      </header>

      <div className="grid items-start gap-7 xl:grid-cols-[210px_minmax(0,1fr)]">
        <aside className="space-y-5 xl:sticky xl:top-6">
          <div className="relative">
            <Search aria-hidden="true" className="absolute top-3 left-3 h-4 w-4 text-ink-3" />
            <Input
              aria-label="搜索操作说明"
              className="bg-card pl-9"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索操作，如：文章"
              type="search"
              value={query}
            />
          </div>
          <nav aria-label="操作说明目录" className="rounded-xl border border-border bg-card p-4">
            <p className="mb-3 text-xs font-semibold text-ink-2">教程目录</p>
            <div className="space-y-4">
              {sections.map((section) => (
                <div key={section.id}>
                  <a
                    className="text-sm font-semibold text-ink hover:text-brand-text"
                    href={`#${section.id}`}
                  >
                    {section.title}
                  </a>
                  <ul className="mt-2 hidden space-y-2 xl:block">
                    {section.steps.map((step) => (
                      <li key={step.id}>
                        <a
                          className="block text-xs leading-5 text-ink-2 hover:text-brand-text"
                          href={`#${step.id}`}
                        >
                          {step.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <a
                className="block text-sm font-semibold text-ink hover:text-brand-text"
                href="#guide-faq"
              >
                常见问题
              </a>
            </div>
          </nav>
        </aside>

        <div className="min-w-0 space-y-10">
          <p aria-live="polite" className="sr-only">
            找到 {sections.reduce((count, section) => count + section.steps.length, 0)} 项操作
          </p>
          {sections.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-ink-2">
                没有找到相关操作，试试「站点」「模型」或「广告」。
              </p>
              <button
                className="mt-3 text-sm font-semibold text-brand-text hover:underline"
                onClick={() => setQuery("")}
                type="button"
              >
                清空搜索，查看全部教程
              </button>
            </div>
          )}
          {sections.map((section) => (
            <section
              aria-labelledby={`${section.id}-title`}
              className="scroll-mt-6"
              id={section.id}
              key={section.id}
            >
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-ink" id={`${section.id}-title`}>
                  {section.title}
                </h2>
                <p className="mt-2 text-sm leading-6 text-ink-2">{section.description}</p>
              </div>
              <div className="space-y-6">
                {section.steps.map((step) => (
                  <GuideStepCard
                    index={GUIDE_SECTIONS.find((item) => item.id === section.id)!.steps.indexOf(
                      step,
                    )}
                    key={step.id}
                    step={step}
                  />
                ))}
              </div>
            </section>
          ))}
          <section aria-labelledby="guide-faq-title" className="scroll-mt-6" id="guide-faq">
            <h2 className="mb-4 text-xl font-semibold text-ink" id="guide-faq-title">
              常见问题
            </h2>
            <div className="divide-y divide-border rounded-2xl border border-border bg-card px-5">
              {GUIDE_FAQS.map((faq) => (
                <details className="py-4" key={faq.question}>
                  <summary className="cursor-pointer text-sm font-medium text-ink">
                    {faq.question}
                  </summary>
                  <p className="mt-3 text-sm leading-7 text-ink-2">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
